import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import use123zapStore from "../../../context/123zapStore";
import { getTokenData, notify } from "../../../shared/utils/utils";
import { consultRegisteredIntegrations } from "../../../services/api/Integrations/listRegisteredIntegrations";
import { resolveIntegrationKey } from "../../../shared/constants/integrations";

const FORM_DATA_BY_INTEGRATION = {
  "Controllr ISP": ["url", "username", "password"],
  HubSoft: ["url", "client_id", "client_secret", "username", "password"],
  SGP: ["url", "app", "token"],
  EliteSoft: ["url", "_user", "_passwd"],
  Sienge: ["url", "username", "password"],
  "IXC Soft": ["url", "user_id", "token"],
  "Bemobi (7AZ)": ["sevaz_base_url", "sevaz_api_key"],
  Bemobi: ["sevaz_base_url", "sevaz_api_key"],
  "7AZ": ["sevaz_base_url", "sevaz_api_key"],
  "MK Solutions": ["url", "api_token", "api_password"],
  Atlaz: ["url", "token"],
  Atlas: ["url", "token"],
  Efi: [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  EFI: [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  "Efí": [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  Voalle: ["url", "username", "password", "client_id", "secret_id", "syndata"],
  CobreFacil: ["app_id", "secret"],
  "Cobre Fácil": ["app_id", "secret"],
  Asaas: ["api_key"],
  "Receita NET": ["token"],
  ReceitaNet: ["token"],
  ISPFY: ["base_url", "token"],
  "LH ISP": ["parceiro_auth_token", "base_url"],
  "Superlógica Assinaturas": ["app_token", "access_token", "base_url"],
  Superlogica: ["app_token", "access_token", "base_url"],
  SuperlogicaAssinaturas: ["app_token", "access_token", "base_url"],
  ssOtica: ["access_token", "base_url"],
};

const FIELD_DISPLAY_LABELS = {
  sevaz_api_key: "Token",
  sevaz_base_url: "URL",
  parceiro_auth_token: "Token do parceiro",
  base_url: "URL base",
  app_token: "App token",
  access_token: "Access token",
  cnpj: "CNPJ",
};

const FIELD_DEFAULT_VALUES = {
  sevaz_base_url: "https://api.7az.com.br/v2/integrations/omnichannel",
};

const INTEGRATION_FIELD_DEFAULT_VALUES = {
  "LH ISP": { base_url: "https://sistema.celeretelecom.com" },
  "Superlógica Assinaturas": { base_url: "https://api.superlogica.net" },
  Superlogica: { base_url: "https://api.superlogica.net" },
  ssOtica: { base_url: "https://app.ssotica.com.br" },
};

const EFI_INTEGRATION_ID = "fa614ff0-0ec1-48d0-bb6c-b99f6cd0b94b";

const FORM_DATA_BY_INTEGRATION_ID = {
  [EFI_INTEGRATION_ID]: FORM_DATA_BY_INTEGRATION.Efi,
};

function getCredentialDisplayName(credential) {
  const label = String(credential?.label || "").trim();
  const integrationTitle = String(credential?.integration_title || "").trim();

  if (credential?.is_default && (!label || label === integrationTitle)) {
    return `${integrationTitle || "Conexão"} - Principal`;
  }

  return label || integrationTitle || "Conexão";
}

function getIntegrationFormFields(integration) {
  if (!integration) return [];

  const byId = FORM_DATA_BY_INTEGRATION_ID[integration.id];
  if (byId) return byId;

  const title = String(integration.title || "").trim();
  const key = String(integration.key || "").trim();
  const normalizedTitle = title.toLowerCase();
  const normalizedKey = key.toLowerCase();

  const directMatch =
    FORM_DATA_BY_INTEGRATION[title] ||
    FORM_DATA_BY_INTEGRATION[key] ||
    FORM_DATA_BY_INTEGRATION[title.toUpperCase()] ||
    FORM_DATA_BY_INTEGRATION[title.toLowerCase()];

  if (directMatch) return directMatch;

  const isEfiAlias =
    normalizedTitle.includes("efi") ||
    normalizedTitle.includes("efí") ||
    normalizedKey === "efi";

  if (isEfiAlias) return FORM_DATA_BY_INTEGRATION.Efi;

  return [];
}

function resolveEndpoint(integrationName) {
  const integrationKey = resolveIntegrationKey(integrationName);
  if (integrationKey) return integrationKey;

  if (integrationName === "SGP") {
    return "sgp";
  } else if (integrationName === "Controllr ISP") {
    return "controllr";
  } else if (integrationName === "HubSoft") {
    return "hubsoft";
  } else if (integrationName === "EliteSoft") {
    return "elitesoft";
  } else if (integrationName === "Sienge") {
    return "sienge";
  } else if (integrationName === "IXC Soft") {
    return "ixc";
  } else if (
    integrationName === "Bemobi (7AZ)" ||
    integrationName === "Bemobi" ||
    integrationName === "7AZ"
  ) {
    return "bemobi";
  } else if (integrationName === "MK Solutions") {
    return "mksolutions";
  } else if (integrationName === "Atlaz" || integrationName === "Atlas") {
    return "atlaz";
  } else if (
    integrationName === "Efi" ||
    integrationName === "EFI" ||
    integrationName === "Efí"
  ) {
    return "efi";
  } else if (integrationName === "Voalle") {
    return "voalle";
  } else if (
    integrationName === "CobreFacil" ||
    integrationName === "Cobre Fácil"
  ) {
    return "cobrefacil";
  } else if (integrationName === "Asaas") {
    return "asaas";
  } else if (
    integrationName === "Receita NET" ||
    integrationName === "ReceitaNet"
  ) {
    return "receitanet";
  } else if (integrationName === "ISPFY") {
    return "ispfy";
  } else if (integrationName === "LH ISP") {
    return "lhisp";
  } else if (
    integrationName === "Superlógica Assinaturas" ||
    integrationName === "Superlogica" ||
    integrationName === "SuperlogicaAssinaturas"
  ) {
    return "superlogica";
  } else if (integrationName === "ssOtica") {
    return "ssotica";
  }
  return null;
}

function buildInitialState(integration) {
  const fields = getIntegrationFormFields(integration);
  const defaults = INTEGRATION_FIELD_DEFAULT_VALUES[integration?.title] || {};
  return fields.reduce((acc, field) => {
    acc[field] = defaults[field] ?? FIELD_DEFAULT_VALUES[field] ?? "";
    return acc;
  }, {});
}

export default function IntegrationCredentialsModal({
  open,
  integration,
  onClose,
}) {
  const [formState, setFormState] = useState({});
  const [connectionName, setConnectionName] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const setAlreadyFetchIntegrations = use123zapStore(
    (state) => state.setAlreadyFetchIntegrations,
  );

  useEffect(() => {
    if (open && integration) {
      setFormState(buildInitialState(integration));
      setConnectionName("");
      setMakeDefault(false);
    }
  }, [open, integration]);

  useEffect(() => {
    if (!open || !integration?.title) return;

    let ignore = false;

    const fetchCredentials = async () => {
      setCredentialsLoading(true);
      try {
        const response = await consultRegisteredIntegrations();
        const payload = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const currentIntegrationKey = resolveIntegrationKey(integration.title);
        const filteredCredentials = payload.filter(
          (item) =>
            resolveIntegrationKey(item?.integration_title) === currentIntegrationKey,
        );

        if (!ignore) {
          setCredentials(filteredCredentials);
        }
      } catch (error) {
        console.error("Erro ao carregar credenciais da integração:", error);
        if (!ignore) setCredentials([]);
      } finally {
        if (!ignore) setCredentialsLoading(false);
      }
    };

    fetchCredentials();

    return () => {
      ignore = true;
    };
  }, [open, integration?.title]);

  const fields = useMemo(() => Object.keys(formState), [formState]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!integration?.title) return;

    const endpoint = resolveEndpoint(integration.title);
    if (!endpoint) {
      notify("Error", "Integração não mapeada para envio", true);
      return;
    }

    setSubmitting(true);
    try {
      const { token, client_id } = await getTokenData();
      if (!token) throw new Error("Token de autenticação não encontrado");
      if (!client_id) throw new Error("ID do cliente não encontrado");

      const trimmedConnectionName = connectionName.trim();
      const payload = trimmedConnectionName
        ? {
            ...formState,
            label: trimmedConnectionName,
            create_new_credential: true,
            is_default: makeDefault,
          }
        : formState;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            Client_ID: `${client_id}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if ((data.message || data.status) === "success") {
        notify("Success", "Integração adicionada com sucesso", true);
        setAlreadyFetchIntegrations(false);
        setCredentials([]);
        onClose();
      } else {
        notify("Error", data?.message || "Erro ao salvar integração", true);
      }
    } catch (error) {
      console.error(error);
      notify("Error", "Erro ao salvar integração", true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 460,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {integration?.title || "Nova integração"}
          </Typography>
          {integration?.icon && (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={integration.icon}
                alt={integration?.title || "logo integração"}
                sx={{
                  width: "76%",
                  height: "76%",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: "6px",
                }}
              />
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important", pb: 2.5 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 0.5 }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151", mb: 1 }}>
              Conexões cadastradas
            </Typography>

            {credentialsLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                  Carregando conexões...
                </Typography>
              </Box>
            ) : credentials.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {credentials.map((credential) => (
                  <Box
                    key={credential.id}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#111827",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getCredentialDisplayName(credential)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                          {credential.status || "active"}
                        </Typography>
                      </Box>
                      {credential.is_default ? (
                        <Chip
                          label="Principal"
                          size="small"
                          sx={{
                            height: 22,
                            bgcolor: "#e8f5ff",
                            color: "#1976d2",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "#6b7280", py: 0.5 }}>
                Nenhuma conexão cadastrada para esta integração.
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            margin="dense"
            label="Nome da conexão"
            placeholder={`Ex: ${integration?.title || "ERP"} - Filial 2`}
            value={connectionName}
            onChange={(event) => setConnectionName(event.target.value)}
            helperText="Opcional. Preencha para criar uma nova conexão nomeada; deixe em branco para atualizar a conexão principal atual."
          />

          <FormControlLabel
            sx={{ mt: 0.5, mb: 0.5 }}
            control={
              <Checkbox
                checked={makeDefault}
                disabled={!connectionName.trim()}
                onChange={(event) => setMakeDefault(event.target.checked)}
              />
            }
            label="Usar como conexão principal"
          />

          {fields.map((field) => (
            <TextField
              key={field}
              fullWidth
              margin="dense"
              label={FIELD_DISPLAY_LABELS[field] || field}
              type={
                field === "password" || field === "sevaz_api_key"
                  ? "password"
                  : "text"
              }
              value={formState[field] || ""}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  [field]: event.target.value,
                }))
              }
            />
          ))}
          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Essa integração não possui campos configurados.
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1.5, mt: 2.5 }}>
            <Button onClick={onClose} variant="outlined" fullWidth>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || fields.length === 0}
            >
              Conectar
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
